import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { AXIOS_API_INSTANCE } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Box, FormControl, FormControlLabel, FormLabel, IconButton, Radio, RadioGroup } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop"
import { DataGrid } from "@mui/x-data-grid"
import { MuiTelInput } from "mui-tel-input"
import { formatPhoneNumber } from "../../Utils"

const columns = [
    {
        field: 'name',
        headerName: "Name",
        flex: 1,
    },
    {
        field: 'company_name',
        headerName: "Company Name",
        flex: 1,
    },
    {
        field: 'email',
        headerName: "Email",
        flex: 1,
    },
    {
        field: 'phone',
        headerName: "Phone",
        flex: 1,
        valueFormatter: (value) => formatPhoneNumber(value)
    }
]

export default function CustomerSearchModal({
    open,
    closeModal,
    returnFilters = false,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [name, setName] = React.useState('')
    const [email, setEmail] = React.useState('')
    const [phone, setPhone] = React.useState('')
    const [isLoading, setIsLoading] = React.useState(false)
    const [searchedCustomers, setSearchedCustomers] = React.useState([])
    const [searchedCustomersOpen, setSearchedCustomersOpen] = React.useState(false)
    const [searchBy, setSearchBy] = React.useState('phone')

    const searchedCustomerClick = (params) => {
        setSearchedCustomersOpen(false)
        closeModal && closeModal(params.row)
    }

    const searchName = (e) => {
        e.preventDefault();
        search({ "or": [{ 'name.ilike': name }, { 'company_name.ilike': name }] })
    }
    const searchEmail = (e) => {
        e.preventDefault();
        search({ 'email.ilike': email })
    }
    const searchPhone = (e) => {
        e.preventDefault();
        if (phone.length < 10) {
            search({ 'or': [{ 'phone.like': phone }, { 'customer_contacts:phone.like': phone }] })
        } else {
            search({ 'or': [{ 'phone.eq': phone }, { 'customer_contacts:phone.eq': phone }] })
        }
    }

    const search = (filter) => {
        if(returnFilters) {
            closeModal(filter, searchBy)
            return
        }
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("customers", {
                params: {
                    page_num: 0,
                    page_size: 100,
                    order_by: 'name',
                    order_by_direction: 'asc',
                    filters: JSON.stringify(filter),
                },
            }).then((customers) => {
                if (customers.data.data.length === 1) {
                    searchedCustomerClick({ 'row': customers.data.data[0] })
                    enqueueSnackbar('Customer found', { variant: 'success' })
                } else if (customers.data.data.length === 0) {
                    enqueueSnackbar('No Customers found', { variant: 'warning' })
                } else {
                    setSearchedCustomers(customers.data.data)
                    setSearchedCustomersOpen(true)
                }
                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                console.log(error)
                enqueueSnackbar(`Failed to search customers <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal && closeModal(false)}
                maxWidth={'lg'}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <LoadingBackdropIndeterminate
                    open={isLoading}
                    text={"Searching Customers"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>Customer Search</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal && closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Grid container direction="column" spacing={2} sx={{ minWidth: '350px' }}>
                            {searchBy === 'name' && <form onSubmit={searchName}>
                                <Grid container alignItems={'center'} spacing={1} size="grow">
                                    <Grid size="grow">
                                        <TextField
                                            margin="dense"
                                            id="name"
                                            label="Name"
                                            type="text"
                                            fullWidth
                                            variant="filled"
                                            value={name || ""}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid size="auto">
                                        <Button aria-label="search" type="submit" variant="contained" disabled={!name} sx={{ p: 1, minWidth: 1 }}>
                                            <SearchIcon />
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                            }
                            {searchBy === 'email' &&
                                <form onSubmit={searchEmail}>
                                    <Grid container alignItems={'center'} spacing={1} size="grow">
                                        <Grid size="grow">
                                            <TextField
                                                margin="dense"
                                                id="email"
                                                label="Email"
                                                type="text"
                                                fullWidth
                                                variant="filled"
                                                value={email || ""}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid size="auto">
                                            <Button aria-label="search" type="submit" variant="contained" disabled={!email} sx={{ p: 1, minWidth: 1 }}>
                                                <SearchIcon />
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            }
                            {searchBy === 'phone' &&
                                <form onSubmit={searchPhone}>
                                    <Grid container alignItems={'center'} spacing={1} size="grow">
                                        <Grid size="grow">
                                            <MuiTelInput
                                                margin="dense"
                                                id="phone"
                                                label="Phone"
                                                type="tel"
                                                fullWidth
                                                variant="filled"
                                                value={phone}
                                                onChange={(e, extra) => {
                                                    try {
                                                        if (extra.nationalNumber !== null && extra.nationalNumber !== undefined && extra.nationalNumber.length < 11) {
                                                            setPhone(extra.nationalNumber)
                                                        }
                                                    } catch (error) {
                                                        console.log(error)
                                                    }
                                                }}
                                                InputLabelProps={{ shrink: true }}
                                                onlyCountries={['US']}
                                                defaultCountry="US"
                                                forceCallingCode
                                                disableDropdown
                                            />
                                        </Grid>
                                        <Grid size="auto">
                                            <Button aria-label="search" type="submit" variant="contained" disabled={!phone} sx={{ p: 1, minWidth: 1 }}>
                                                <SearchIcon />
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            }
                            <Grid size="grow">
                                <FormControl>
                                    <FormLabel id="customer-search-radio-buttons-group-label">Search By</FormLabel>
                                    <RadioGroup
                                        aria-labelledby="customer-search-radio-buttons-group-label"
                                        name="radio-buttons-group"
                                        value={searchBy}
                                        onChange={(e) => setSearchBy(e.target.value)}
                                    >
                                        <FormControlLabel value="phone" control={<Radio />} label="Phone" />
                                        <FormControlLabel value="email" control={<Radio />} label="Email" />
                                        <FormControlLabel value="name" control={<Radio />} label="Name" />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
            </Dialog>
            <Dialog
                open={searchedCustomersOpen}
                onClose={() => setSearchedCustomersOpen(false)}
                maxWidth={'lg'}
                fullWidth
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitle>
                    Customers
                    <IconButton
                        aria-label="close"
                        onClick={() => setSearchedCustomersOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DataGrid
                        rows={searchedCustomers ?? []}
                        columns={columns}
                        autoHeight={true}
                        onRowClick={searchedCustomerClick}
                        hideFooter
                        disableColumnMenu
                        disableColumnSelector
                        disableColumnFilter
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'name', sort: 'asc' }],
                            },
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>)
    );
}
